import type {
  PjazzaAccountCustomerResponse,
  PjazzaAccountOrdersResponse
} from '@/domains/pjazza/types/models/PjazzaAccount.types.ts'
import type {
  PjazzaAccountReservationsResponse,
  PjazzaNearestReservationAvailabilityResponse,
  PjazzaReservationAvailabilityResponse,
  PjazzaReservationResponse,
  PjazzaTonightOffersResponse
} from '@/domains/pjazza/types/models/PjazzaReservation.types.ts'
import type {
  PjazzaRoundResponse,
  PjazzaTableGroupsResponse,
  PjazzaTabResponse
} from '@/domains/pjazza/types/models/PjazzaTab.types.ts'
// Payload types
import type {
  OrderingDirectoryIndexPayload,
  OrderingOrderStatusPayload,
  OrderingOtpRequestPayload,
  OrderingOtpVerifyPayload,
  OrderingPlaceTakeawayPayload,
  OrderingQuotePayload,
  PjazzaAvailabilityPayload,
  PjazzaCancelReservationPayload,
  PjazzaPlaceReservationPayload,
  PjazzaTabCheckoutPayload,
  PjazzaTabFirePayload,
  PjazzaTabRoundPayload,
  PjazzaTabShowPayload,
  PjazzaVenueMenuShowPayload,
  PjazzaVenueTablesPayload
} from '@/domains/pjazza/types/requests/EndpointRequest.types.ts'
import type {
  OrderingCheckoutResponse,
  OrderingCheckoutStatusResponse,
  OrderingOrderStatusResponse,
  OrderingOtpRequestResponse,
  OrderingPlaceOrderResponse,
  OrderingQuoteResponse,
  OrderingSessionResponse,
  PjazzaTabCheckoutResponse
} from '@/domains/pjazza/types/responses/OrderingOrderResponse.types.ts'
// Response types
import type { OrderingDirectoryIndexResponse } from '@/domains/pjazza/types/responses/PjazzaDirectoryIndexResponse.types.ts'
import type { PjazzaVenueMenuResponse } from '@/domains/pjazza/types/responses/PjazzaVenueMenuResponse.types.ts'

import { twineServiceClient } from '@/services/api'
import { relativeZiggyRoute } from '@/utils/url-util/urlUtil.ts'

/**
 * Public site directory API service (consumer ordering app)
 */
const callOrderingDirectory = () => {
  /**
   * List every site enabled for online ordering
   */
  const index = (payload: OrderingDirectoryIndexPayload = {}) => {
    return twineServiceClient.get<OrderingDirectoryIndexResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.directory.index'),
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  return {
    index
  }
}

/**
 * Public site menu API service (consumer ordering app)
 */
const callOrderingMenus = () => {
  /**
   * Get the published menu for a site by its public slug
   */
  const show = (payload: PjazzaVenueMenuShowPayload) => {
    return twineServiceClient.get<PjazzaVenueMenuResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.sites.menu', { site: payload.siteSlug }),
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  return {
    show
  }
}

/**
 * Ordering API service — pricing, placement and status (consumer app)
 */
const callOrderingOrders = () => {
  /**
   * Price and validate a basket without persisting it. A 422 carries
   * per-line errors keyed by payload path (items.0.addons).
   */
  const quote = (payload: OrderingQuotePayload) => {
    return twineServiceClient.post<OrderingQuoteResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.quote', { site: payload.siteSlug }),
      body: { items: payload.items },
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Place a takeaway order. Requires an ordering session token.
   */
  const placeTakeaway = (payload: OrderingPlaceTakeawayPayload) => {
    return twineServiceClient.post<OrderingPlaceOrderResponse | OrderingCheckoutResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.orders.takeaway', { site: payload.siteSlug }),
      body: {
        items: payload.items,
        notes: payload.notes,
        name: payload.name,
        email: payload.email,
        due_at: payload.dueAt,
        idempotency_key: payload.idempotencyKey
      },
      auth: true,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Poll a placed order. The status token stands in for a login — guests
   * never authenticate, so possession of the token is the authorisation.
   */
  const status = (payload: OrderingOrderStatusPayload) => {
    return twineServiceClient.get<OrderingOrderStatusResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.orders.status', {
        site: payload.siteSlug,
        order: payload.orderId
      }),
      queryParams: { token: payload.statusToken },
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Ask about a checkout again. The server confirms it with the provider while
   * it is still open, so a poll is what settles a payment that was processing.
   */
  const checkoutStatus = (payload: { siteSlug: string; reference: string }) => {
    return twineServiceClient.get<OrderingCheckoutStatusResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.checkouts.status', {
        site: payload.siteSlug,
        checkout: payload.reference
      })
    })
  }

  return {
    quote,
    placeTakeaway,
    status,
    checkoutStatus
  }
}

/**
 * Phone verification API service (consumer ordering app)
 */
const callOrderingVerification = () => {
  /**
   * Text a one-time code to the diner.
   */
  const request = (payload: OrderingOtpRequestPayload) => {
    return twineServiceClient.post<OrderingOtpRequestResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.otp.request', { site: payload.siteSlug }),
      body: { phone: payload.phone },
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Trade a correct code for a short-lived ordering session token.
   */
  const verify = (payload: OrderingOtpVerifyPayload) => {
    return twineServiceClient.post<OrderingSessionResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.otp.verify', { site: payload.siteSlug }),
      body: {
        phone: payload.phone,
        code: payload.code,
        device_name: payload.deviceName
      },
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  return {
    request,
    verify
  }
}

/**
 * Dine-in tab API service (consumer Pjazza app)
 */
const callPjazzaTabs = () => {
  /**
   * Resolve a scanned session code into the table and everything sent to it.
   * A 404 means the tab has been settled or timed out.
   */
  const show = (payload: PjazzaTabShowPayload) => {
    return twineServiceClient.get<PjazzaTabResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.sessions.show', {
        site: payload.siteSlug,
        sessionToken: payload.sessionToken
      }),
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Send a round to the kitchen. Possession of the session code is the
   * authorisation — a dine-in diner never signs in.
   */
  /**
   * Tables a diner may pick from. Only served in table_picker mode.
   */
  const tables = (payload: PjazzaVenueTablesPayload) => {
    return twineServiceClient.get<PjazzaTableGroupsResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.tables.index', { site: payload.siteSlug }),
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  const placeRound = (payload: PjazzaTabRoundPayload) => {
    return twineServiceClient.post<PjazzaRoundResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.rounds.store', { site: payload.siteSlug }),
      body: {
        session_token: payload.sessionToken,
        table_id: payload.tableId,
        items: payload.items,
        notes: payload.notes,
        name: payload.name,
        idempotency_key: payload.idempotencyKey
      },
      // A round is signed for, like a takeaway order: the table code says where
      // you are sitting, the token says who is ordering.
      auth: true,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Fire everything held on the tab to the kitchen. Returns the refreshed
   * session/tab so the caller can show the rounds flipping from held to sent
   * without waiting for the poll. A 409 means the kitchen has stopped taking
   * orders; a 404 means the tab is gone.
   */
  const fireTab = (payload: PjazzaTabFirePayload) => {
    return twineServiceClient.post<PjazzaTabResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.rounds.fire', { site: payload.siteSlug }),
      body: {
        session_token: payload.sessionToken
      },
      // Same authorisation as placing a round: the token says who is firing.
      auth: true,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * "Pay and send" at a venue that charges before the kitchen starts.
   *
   * Returns the payment page for the table's held total. Paying is what fires
   * the kitchen, so the caller never also calls fireTab — the server does it
   * when the money lands.
   */
  const openTabCheckout = (payload: PjazzaTabCheckoutPayload) => {
    return twineServiceClient.post<PjazzaTabCheckoutResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.rounds.checkout', { site: payload.siteSlug }),
      body: {
        session_token: payload.sessionToken
      },
      auth: true,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  return {
    show,
    tables,
    placeRound,
    fireTab,
    openTabCheckout
  }
}

/**
 * Table-booking API service (consumer Pjazza app).
 *
 * Browsing slots is public, like browsing the menu; booking and cancelling
 * carry the platform session token, like placing a takeaway order.
 */
const callPjazzaReservations = () => {
  /**
   * The slots a party of this size can book on this date. Empty when the
   * venue has nothing free — that is an answer, not an error.
   */
  const availability = (payload: PjazzaAvailabilityPayload) => {
    return twineServiceClient.get<PjazzaReservationAvailabilityResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.reservations.availability', { site: payload.siteSlug }),
      queryParams: { date: payload.date, party_size: payload.partySize },
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * D1: the page's own first screen, before the diner has answered anything
   * — tonight's own two slots, then tomorrow evening's first, assumed for
   * two throughout. No date or party on the wire; there is nothing yet to ask.
   */
  const tonightOffers = (payload: PjazzaVenueMenuShowPayload) => {
    return twineServiceClient.get<PjazzaTonightOffersResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.reservations.availability.tonight', { site: payload.siteSlug }),
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * D4: the day just asked for came back with nothing, so this asks the
   * fortnight either side instead — same party, same public throttle group,
   * a tighter limit since it prices many more slots per call.
   */
  const nearestAvailability = (payload: PjazzaAvailabilityPayload) => {
    return twineServiceClient.get<PjazzaNearestReservationAvailabilityResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.reservations.availability.nearest', { site: payload.siteSlug }),
      queryParams: { date: payload.date, party_size: payload.partySize },
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Book the table. A 422 keyed on `time` means the slot filled up between
   * the availability read and this write — re-fetch and let the diner pick
   * again rather than surfacing it as a failure of theirs.
   */
  const place = (payload: PjazzaPlaceReservationPayload) => {
    return twineServiceClient.post<PjazzaReservationResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.reservations.place', { site: payload.siteSlug }),
      body: {
        date: payload.date,
        time: payload.time,
        party_size: payload.partySize,
        name: payload.name,
        email: payload.email,
        notes: payload.notes,
        area_preference_id: payload.areaPreferenceId,
        occasion: payload.occasion,
        needs: payload.needs
      },
      auth: true,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Cancel the diner's own booking. Deliberately not gated on the venue's
   * reservations toggle server-side: a venue that turns bookings off still
   * owes the diner the ability to cancel one it already holds.
   */
  const cancel = (payload: PjazzaCancelReservationPayload) => {
    return twineServiceClient.post<PjazzaReservationResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.reservations.cancel', {
        site: payload.siteSlug,
        reservation: payload.reservationId
      }),
      body: {},
      auth: true,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /** The diner's bookings across every venue — platform-level, like me/orders. */
  const mine = () => {
    return twineServiceClient.get<PjazzaAccountReservationsResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.account.reservations'),
      auth: true
    })
  }

  return {
    availability,
    tonightOffers,
    nearestAvailability,
    place,
    cancel,
    mine
  }
}

/**
 * Platform account API service — sign in and cross-venue order history.
 * Tenant-less: identity and the order index both live in Pjazza.
 */
const callPjazzaAccount = () => {
  const requestCode = (payload: { phone: string }) => {
    return twineServiceClient.post<OrderingOtpRequestResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.account.sign-in.request'),
      body: { phone: payload.phone }
    })
  }

  const verifyCode = (payload: { phone: string; code: string }) => {
    return twineServiceClient.post<OrderingSessionResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.account.sign-in.verify'),
      body: { phone: payload.phone, code: payload.code, device_name: 'pjazza-web' }
    })
  }

  const orders = () => {
    return twineServiceClient.get<PjazzaAccountOrdersResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.account.orders'),
      auth: true
    })
  }

  /**
   * Correct the name and email held against the account. The phone is
   * absent on purpose — it is the account, and changing it is a
   * re-verification rather than an edit.
   */
  const updateDetails = (payload: { name?: string | null; email?: string | null }) => {
    return twineServiceClient.patch<PjazzaAccountCustomerResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.account.update'),
      // Only what the caller named. An absent key means "leave it alone" and a
      // null one means "clear it" — signing up sends a name and must not wipe
      // an email given at a checkout months ago.
      body: payload,
      auth: true
    })
  }

  /**
   * A diner's own photo. Multipart, so the client sets no Content-Type and
   * lets the browser write the boundary.
   */
  const uploadAvatar = (file: File) => {
    const body = new FormData()

    body.append('avatar', file)

    return twineServiceClient.post<PjazzaAccountCustomerResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.account.avatar.store'),
      body,
      auth: true
    })
  }

  const removeAvatar = () => {
    return twineServiceClient.delete<PjazzaAccountCustomerResponse>({
      endpoint: relativeZiggyRoute('pjazza.api.account.avatar.destroy'),
      auth: true
    })
  }

  /**
   * Hand this device's token back so the server stops honouring it.
   *
   * Only this device's — the same diner's other phone stays signed in. The
   * caller does not wait on it: signing out has to work offline, so the app
   * clears itself either way and this is the half that can fail.
   */
  const signOut = () => {
    return twineServiceClient.delete<null>({
      endpoint: relativeZiggyRoute('pjazza.api.account.session.destroy'),
      auth: true
    })
  }

  const deleteAccount = () => {
    return twineServiceClient.delete<null>({
      endpoint: relativeZiggyRoute('pjazza.api.account.delete'),
      auth: true
    })
  }

  return {
    requestCode,
    verifyCode,
    orders,
    updateDetails,
    uploadAvatar,
    removeAvatar,
    signOut,
    deleteAccount
  }
}

export {
  callOrderingDirectory,
  callOrderingMenus,
  callOrderingOrders,
  callOrderingVerification,
  callPjazzaAccount,
  callPjazzaReservations,
  callPjazzaTabs
}
